import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money } from '../../utils/region';
import { coaTypeApi, ChartKind, ChartVocabulary, TypeOptions, ParentOptions, RoleOptions } from '../../utils/api/coatype';
import AccountHistory from './AccountHistory';
import { TypeBadge, ControlBadge } from './AccountTypeBadge';
import { RoleBadge, MappedRoleBadge } from './AccountRoleBadge';
import TemplateSync from './TemplateSync';
import { coaSyncApi } from '../../utils/api/coasync';
// b13 S2 (B13-15): the read-only seats through the one helper (books AND auditor), never a role literal.
import { useReadOnlySeat } from '../../utils/readOnlySeat';

// ── A client's chart of accounts ────────────────────────────────────────────────────────────────
// The Accounting Act does not mandate a chart as such. It is implied: s.7 lets the DBD prescribe
// the books, the 2544 notification requires ledgers that separate assets/liabilities/capital,
// income/expense, debtors and creditors, and s.21(1) requires a Thai code translation manual if
// entries carry codes. The Thai name IS that manual, which is why it cannot be left blank.
//
// An account that has been posted to is frozen in code and class — printed books cite them — but
// its name can always be corrected, and it can be switched off so it stops being offered.
//
// Batch 5 round 4 (COA-6 mechanism / COA-11): each account also carries the customer's TYPE word, a
// GROUP and a DESCRIPTION, edited here ("Edit"). ⛔ The screen holds no chart rule: `kind` and
// `normalSide` come on every row from the server (server/src/services/coaRules.ts), and which type
// words an account may take — with the reason a word is refused — come from GET /chart/type-options.
// The type and group words are the customer's own and are never translated.
//
// b13 S2 (COA-6's parent half): an account may sit under a PARENT account. The tree comes from the server — each row's
// parent_id, depth and live sub-accounts, in ONE depth-first order (server/src/services/accountingSetup.ts chartOf) — and
// is drawn indented inside its class; ⛔ the screen never infers a parent from a code. Add and Edit carry a Parent select
// whose choices are GET /chart/parent-options (each simulated through the tree rules, so the select offers only what the
// door takes); an account that cannot leave its parent (a Header's last sub-account) shows the reason instead of a
// select. The class is not offered on an account inside a tree (any other class would be refused), Remove only where the
// server's own verdict (remove_refusal) says it would be taken, and a parent stored behind the doors that breaks a rule
// is named on its row (tree_problem).
//
// Batch 5 round 7 (COA-4 for the live template / COA-2): an EMPTY chart is still started from the firm's
// template with one click by any accounting login (first initialisation; O8). On a chart that exists,
// "Sync from template" no longer copies anything — it opens a preview (TemplateSync.tsx) of the template
// accounts the chart does not have, and only an administrator may add the ones ticked (O7). A template
// account this chart removed or renumbered is never offered again; one whose code another account here
// holds is listed apart and cannot be ticked. The server decides all of it.
//
// Batch 8 R1 (design §3.2, D1): a ROLE column — what the system itself posts to each account for (output VAT, retained
// earnings, the parking accounts …), as the server sends it on every row (roleEffective / roleSource: an administrator's
// 'set', or the firm template's own, drawn light "from the template"), with the payroll / WHT posting maps' answers beside
// it read-only (roleMapped, D3). Everyone reads it. Only a login the SERVER says may change a role (GET /chart's
// canEditRole — an administrator) is drawn the Role select in Edit, whose choices are GET /chart/role-options for the class
// the form says (each refused one greyed with its reason); anyone else is drawn no role control and never asks for them.
// An account that holds a role is offered only the classes that role stands on (roleClasses). ⛔ No role is worked out
// from a code or a name here.

type Account = {
  id: string; code: string; name_th: string; name_en: string; class: string; control: string; active: number; lines: number; net_dr: number; cf_section: string; fs_section: string;
  account_type: string; account_group: string; description: string; kind: ChartKind; normalSide: string;
  /** (Batch 11 W4) How many petty cash boxes hold the account: such an account is never removed (switched off instead). */
  boxes?: number;
  /** b13 S2: the stored parent ('' = top level), how deep the server's walk puts it, its live sub-accounts, whether Remove
   *  would be taken (remove_refusal '' = yes; else the rule, with the server's sentence) and a stored tree problem ('' = none). */
  parent_id?: string; depth?: number; children?: number; remove_refusal?: string; remove_message?: string; tree_problem?: string;
  /** Batch 8 R1: its own role ('' = none), the role it holds and from where ('set' / 'template'), the classes that role
   *  stands on ([] = none held), and the payroll / settlement maps' answers naming it (read only). */
  role?: string; roleEffective?: string; roleSource?: string; roleClasses?: string[]; roleMapped?: { source: string; role: string }[];
};
type Edit = { nameTh: string; nameEn: string; cfSection: string; fsSection: string; cls: string; type: string; group: string; description: string; parentId: string; role: string };

// A presentation choice this client's own books have raised — GET /accounting/chart/placements, which
// re-serves services/accountingSetup.ts's placementQuestions(). The server sends its own th/en
// wording for callers that have no translation layer; this screen has one, so it shows the keys
// below and uses only the FIGURES and the account from the question.
type Question = { accountId: string; code: string; nameTh: string; nameEn: string; cls: string; amount: number };

// Label keys only — t() is hook-bound and cannot be called at module scope.
const CLASS_KEY: Record<string, string> = {
  asset: 'pa_chart_class_asset', liability: 'pa_chart_class_liability', equity: 'pa_chart_class_equity',
  income: 'pa_chart_class_income', expense: 'pa_chart_class_expense',
};
const ORDER = ['asset', 'liability', 'equity', 'income', 'expense'];
// Where this account's movement lands on the cash flow statement.
const CF = [
  { k: '', key: 'pa_chart_cf_none' },
  { k: 'operating', key: 'pa_chart_cf_operating' },
  { k: 'investing', key: 'pa_chart_cf_investing' },
  { k: 'financing', key: 'pa_chart_cf_financing' },
  { k: 'cash', key: 'pa_chart_cf_cash' },
  { k: 'noncash', key: 'pa_chart_cf_noncash' },
];
// A balance-sheet account with no section is the one that actually breaks the statement: its
// movement is not inside the profit the indirect method starts from, so leaving it out of every
// total makes the net change miss the real cash movement by exactly that amount. An income or
// expense account without one is only untidy.
const cfMatters = (cls: string) => ['asset', 'liability', 'equity'].includes(cls);
// Where this account's money sits on บัญชีทำการ — services/accountingSetup.ts's FS_SECTION_VALUES.
// '' is a real third state meaning nobody has said, and the statement reports such an account with
// its amount instead of folding it into either total.
const FS = [
  { k: '', key: 'pa_chart_fs_none' },
  { k: 'trading', key: 'pa_chart_fs_trading' },
  { k: 'other', key: 'pa_chart_fs_other' },
];
// The mirror of cfMatters, and the opposite way round: บัญชีทำการ is built from income and expense,
// so a balance-sheet account has no placement on it at all (the server refuses one).
const fsMatters = (cls: string) => ['income', 'expense'].includes(cls);
const baht = (s: number) => money(Number(s || 0) / 100);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '5px 8px', fontSize: 12, borderRadius: 6, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};

// `defaultOpen` (owner 2026-10-13): inside StatutoryBooks the chart is one of a dozen panels and starts
// folded; as the workspace's own Chart of Accounts page a folded list would be an empty screen. Default
// stays false, so every existing mount is unchanged.
export default function ChartOfAccounts({ clientId, defaultOpen = false }: { clientId: string; defaultOpen?: boolean }) {
  const { t, i18n } = useTranslation();
  // ⛔ The books login (a client's own people) is a READ-ONLY seat: server/src/middleware.ts refuses
  // every write it sends, by method. So it is not offered one — no first copy, no Add account, no Edit,
  // no Switch off, no Remove, no placement answer — because a control whose only possible answer is a
  // 403 with a JSON body is also exactly the shape server/src/index.ts pages the admins with. The same
  // rule as CompanyWorkspace/panes/Documents.tsx. The server stays the gate; this is only what is drawn.
  // It still reads everything: the chart, the badges, the search and History (whose drawer answers for
  // itself). ⛔ Not "Sync from template" (owner 2026-10-20): no button and no preview — adding template
  // accounts is an administrator's approval, refused to this login on the server, so it is not shown one.
  const readOnly = useReadOnlySeat();
  const [rows, setRows] = useState<Account[] | null>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [adding, setAdding] = useState(false);
  const EMPTY_DRAFT = { code: '', nameTh: '', nameEn: '', cls: 'expense', cfSection: '', fsSection: '', type: '', group: '', description: '', parentId: '' };
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editId, setEditId] = useState('');
  const [edit, setEdit] = useState<Edit>({ nameTh: '', nameEn: '', cfSection: '', fsSection: '', cls: '', type: '', group: '', description: '', parentId: '', role: '' });
  // Batch 8 R1: whether this login may change a role (the server's answer on GET /chart), and — for the account being
  // edited, asked only then — which roles it may take for the class its form says (GET /chart/role-options).
  const [canEditRole, setCanEditRole] = useState(false);
  const [editRoles, setEditRoles] = useState<RoleOptions | null>(null);
  // The words a type and a group may be (the 16 type words; the Subgroup words + this client's own),
  // and — for the account being edited and for the add form — which type words the server's rules allow.
  const [vocab, setVocab] = useState<ChartVocabulary | null>(null);
  const [editOpts, setEditOpts] = useState<TypeOptions | null>(null);
  const [addOpts, setAddOpts] = useState<TypeOptions | null>(null);
  // b13 S2: the Parent select's choices (GET /chart/parent-options) for the add form and for the account being edited.
  const [addParents, setAddParents] = useState<ParentOptions | null>(null);
  const [editParents, setEditParents] = useState<ParentOptions | null>(null);
  const [query, setQuery] = useState('');
  const [onlyUnclassified, setOnlyUnclassified] = useState(false);
  // ⛔ Starts empty, and stays empty for a client whose books have raised nothing. The prompt below
  // only ever appears when the server sends a question, which it does only for a client that has
  // actually written inventory down and has not yet answered — never for the other ninety-seven.
  const [questions, setQuestions] = useState<Question[]>([]);
  // The account whose change history is open in the drawer (COA-10) — one at a time.
  const [historyOf, setHistoryOf] = useState<Account | null>(null);
  // The template preview (batch 5 round 7) is open.
  const [syncing, setSyncing] = useState(false);
  // A chart that shows no account but HAS rows — every one of them removed. It is not empty in the
  // server's sense (a deleted row counts), so it is not started again from the template; the screen
  // says so instead of offering a first copy the server would refuse. Asked only when nothing shows.
  const [removedAll, setRemovedAll] = useState(false);

  const load = useCallback(async () => {
    let shown = -1;
    try { const d: any = await api.acctChart(clientId); setRows(d.accounts || []); shown = (d.accounts || []).length; setCanEditRole(d.canEditRole === true); }
    catch (e: any) { setErr(e.message); setRows([]); setCanEditRole(false); }
    // ⛔ Not for a books login: template-diff IS the Sync preview (every template account the chart lacks),
    // which this login is not shown (owner 2026-10-20) — so it is not fetched on its behalf either. Its
    // empty chart reads as empty; "all removed" only matters to a login that can add or approve.
    if (shown === 0 && !readOnly) {
      try { setRemovedAll((await coaSyncApi.diff(clientId)).chartRows > 0); } catch { setRemovedAll(false); }
    } else setRemovedAll(false);
    // The group words for the datalist. A failure leaves the group a plain text box, nothing worse.
    try { setVocab(await coaTypeApi.vocabulary(clientId)); } catch { setVocab(null); }
    // A chart that loaded is worth showing even if this call fails, so a failure here leaves the
    // chart alone and raises no prompt. Nothing is hidden by that: the money stays on the statement,
    // which lists the account with its amount and warns, exactly as it does while unanswered.
    try { const q: any = await api.acctPlacements(clientId); setQuestions(q.questions || []); }
    catch { setQuestions([]); }
  }, [clientId, readOnly]);
  useEffect(() => { load(); }, [load]);

  /** Record a reply to one presentation question. No placement is pre-selected and none is implied
   *  by the order the two appear in. */
  const answer = async (q: Question, placement: string) => {
    setBusy(q.accountId); setErr('');
    try { await api.acctAnswerPlacement(clientId, q.accountId, placement); await load(); }
    catch (e: any) { setErr(e.message || t('pa_chart_wd_failed')); } finally { setBusy(''); }
  };

  const copy = async () => {
    setBusy('copy'); setErr('');
    try { const r: any = await api.acctCopyTemplate(clientId); await load(); if (!r.copied) setErr(t('pa_chart_template_complete')); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  const downloadPdf = async () => {
    setBusy('pdf'); setErr('');
    try { await api.acctChartPdf(clientId); }
    catch (e: any) { setErr(e.message || t('pa_chart_download_failed')); }
    finally { setBusy(''); }
  };
  // Which type words a NEW account of the form's class may take — asked again when the class changes.
  useEffect(() => {
    if (!adding) return;
    let live = true;
    coaTypeApi.typeOptions(clientId, { cls: draft.cls }).then(o => { if (live) setAddOpts(o); }).catch(() => { if (live) setAddOpts(null); });
    return () => { live = false; };
  }, [adding, clientId, draft.cls]);
  // (R1 ship gate) What the Edit form says that the stored row does not: the role (only from a login that may change one)
  // and the type — each sent to the other's options only when it differs (unchanged, the server reads the stored value,
  // which is the same answer), so the type select never offers a word the form's role is refused with, nor the role
  // select a role the form's type is refused with.
  const editStored = editId ? (rows || []).find(r => r.id === editId) : undefined;
  const formRole = canEditRole && editStored && edit.role !== (editStored.role || '') ? edit.role : undefined;
  const formType = editStored && edit.type !== (editStored.account_type || '') ? edit.type : undefined;
  // …and which ones the account being edited may take, for the class its form now says — and (R1 ship gate) for the role
  // its form now says: a role's account takes no type nothing is posted to and no Contra type, so the words a save with
  // that role would be refused for are greyed (the server's answer, never worked out here).
  useEffect(() => {
    if (!editId) { setEditOpts(null); return; }
    let live = true;
    coaTypeApi.typeOptions(clientId, { accountId: editId, cls: edit.cls, role: formRole })
      .then(o => { if (live) setEditOpts(o); }).catch(() => { if (live) setEditOpts(null); });
    return () => { live = false; };
  }, [editId, clientId, edit.cls, formRole]);
  // Batch 8 R1: which roles the account being edited may take, for the class (and, R1 ship gate, the type) its form now
  // says — asked only for a login the server lets change a role. A role picked in this form that the new class or type
  // refuses is put back to the stored one (the select never holds a choice Save would be refused for).
  useEffect(() => {
    setEditRoles(null);   // never another account's (or another class's) answer while this one is on its way
    if (!editId || !canEditRole) return;
    let live = true;
    coaTypeApi.roleOptions(clientId, editId, edit.cls, formType).then(o => {
      if (!live) return;
      setEditRoles(o);
      setEdit(e => {
        const op = o.options.find(x => x.role === e.role);
        return e.role && e.role !== o.current && op?.refused ? { ...e, role: o.current } : e;
      });
    }).catch(() => { if (live) setEditRoles(null); });
    return () => { live = false; };
  }, [editId, clientId, edit.cls, formType, canEditRole]);
  // b13 S2: which accounts a NEW account of the form's class may sit under — asked again when the class changes (the
  // chosen parent is cleared then: a parent of another class is a refusal).
  useEffect(() => {
    if (!adding) { setAddParents(null); return; }
    let live = true;
    coaTypeApi.parentOptions(clientId, { cls: draft.cls }).then(o => { if (live) setAddParents(o); }).catch(() => { if (live) setAddParents(null); });
    return () => { live = false; };
  }, [adding, clientId, draft.cls]);
  // …and which ones the account being edited may sit under (or why it cannot move at all).
  useEffect(() => {
    if (!editId) { setEditParents(null); return; }
    let live = true;
    coaTypeApi.parentOptions(clientId, { accountId: editId, cls: edit.cls }).then(o => { if (live) setEditParents(o); }).catch(() => { if (live) setEditParents(null); });
    return () => { live = false; };
  }, [editId, clientId, edit.cls]);

  const add = async () => {
    setBusy('add'); setErr('');
    // A placement is only ever sent for a class that can hold one. The server refuses the other
    // classes outright, and a form that switched from expense to asset with a placement still in
    // state would be refused for a field the operator could no longer see.
    try {
      // b13 S2: the parent only when one was chosen ('' = the top level, which is what an add without it means).
      const { parentId, ...rest } = draft;
      await api.acctAddAccount(clientId, { ...rest, fsSection: fsMatters(draft.cls) ? draft.fsSection : '', ...(parentId ? { parentId } : {}) });
      setDraft(EMPTY_DRAFT); setAdding(false); await load();
    }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const startEdit = (a: Account) => {
    setEditId(a.id);
    setEdit({ nameTh: a.name_th, nameEn: a.name_en, cfSection: a.cf_section || '', fsSection: a.fs_section || '', cls: a.class,
      type: a.account_type || '', group: a.account_group || '', description: a.description || '', parentId: a.parent_id || '', role: a.role || '' });
  };
  const saveEdit = async (orig: Account) => {
    setBusy(orig.id); setErr('');
    // The class the form now says — it can only differ while nothing is posted (the class picker is
    // offered only then, and the server freezes a posted class anyway).
    const a = { ...orig, class: edit.cls || orig.class };
    // fsSection is only SENT for a class that can hold one. Sending '' for the others would be a
    // write of its own: it would silently clear a placement nobody meant to touch.
    const rest: Partial<Edit> = { ...edit };
    delete rest.fsSection;
    const body: Partial<Edit> = { ...(fsMatters(a.class) ? edit : rest) };
    // b13 S2: the parent only when it MOVED — a save about a name sends no parentId (the door reads it only when said).
    if (body.parentId === (orig.parent_id || '')) delete body.parentId;
    // Batch 8 R1: the role only when it CHANGED, and only from a login the server lets change one (anyone else's PATCH
    // saying a role is a 403 — so it never says one).
    if (!canEditRole || body.role === (orig.role || '')) delete body.role;
    try { await api.acctUpdateAccount(a.id, body); setEditId(''); await load(); }
    catch (e: any) { setErr(saveError(e, body.role)); } finally { setBusy(''); }
  };
  /** Batch 8 R1: a role reason in this language — the server's rule (services/coaRules.ts ROLE_RULES) as coar_why_<rule>,
   *  with the classes the role stands on and the account holding it, both from GET /chart/role-options. */
  const roleWhy = (rule: string, role: string) => {
    // '' is taking the account's own role off (the answer's `none`); any other value is one of the roles.
    const op = role === '' ? null : editRoles?.options.find(o => o.role === role);
    // (R1 review) A leave rule names the role this account would lose and the copy it would go back to — never a code of
    // the screen's own: both come from GET /chart/role-options.
    const leaves = role === '' ? editRoles?.none?.leaves : op?.leaves;
    if (leaves && (rule === 'role-orphaned' || rule === 'role-fallback' || rule === 'role-inherited')) {
      const lost = editRoles?.options.find(o => o.role === leaves.role);
      const fb = leaves.fallback;
      return t(`coar_why_${rule.replace(/-/g, '_')}`, {
        held: t(`coar_role_${leaves.role}`, { defaultValue: lost?.label || leaves.role }), code: fb?.code || '',
        why: fb ? t(`coar_fb_${fb.rule.replace(/-/g, '_')}`, { classes: (lost?.classes || []).map(c => t(CLASS_KEY[c] || c)).join(' / ') }) : '' });
    }
    // (R1 review, second pass) A cash or bank account says which (marked cash / paired with a bank account / a petty cash
    // box's), from what the server weighed; a move refused by its drafts names them and the account they sit on.
    if (rule === 'role-cash') {
      const c = op?.facts?.cash;
      return c ? t(`coar_why_role_cash_${c.why}`, { name: c.name || '', defaultValue: t('coar_why_role_cash') }) : t('coar_why_role_cash');
    }
    if (rule === 'role-drafts') {
      const moves = role === '' ? editRoles?.none?.moves : op?.moves;
      const m = moves?.find(x => x.drafts.length);
      // (R1 re-verify, v3) …or by the drafts on the account the role would go TO (their lines would post into the role).
      const mt = m ? null : moves?.find(x => (x.toDrafts || []).length);
      if (mt) return t('coar_why_role_drafts_to', { n: mt.toDrafts?.length || 0, code: mt.to || '', docs: (mt.toDrafts || []).slice(0, 10).map(d => d || '—').join(', ') });
      return t('coar_why_role_drafts', { n: m?.drafts.length || 0, code: m?.from || '', docs: (m?.drafts || []).slice(0, 10).map(d => d || '—').join(', ') });
    }
    // (R1 re-verify, v3) A posting setting, an employee's own salary account or a per-charge memory says which, from what the
    // server weighed (the memory's charge or supplier, the payroll item, the run) — never worked out here.
    if (rule === 'role-mapped') {
      const f = op?.facts?.mapped;
      const name = f?.source === 'sale_role_memory' ? t(`coar_sale_role_${f.role}`, { defaultValue: f.name || f.role }) : f?.name || '';
      return f ? t(`coar_why_role_mapped_${f.source}`, { name, defaultValue: t('coar_why_role_mapped') }) : t('coar_why_role_mapped');
    }
    // (R1 ship gate) The fixed-asset register's account says who names it (a category, an asset, a disposal) and for what;
    // a pinned role says the account it stays on (or that the chart has none); a contra account names its type.
    if (rule === 'role-fixed-asset') {
      const f = op?.facts?.asset;
      return f ? t(`coar_why_role_fixed_asset_${f.source}`, { name: f.name, what: t(`coar_fa_what_${f.what}`), defaultValue: t('coar_why_role_fixed_asset') })
        : t('coar_why_role_fixed_asset');
    }
    if (rule === 'role-pinned') {
      const label = t(`coar_role_${role}`, { defaultValue: op?.label || role });
      return op?.pinnedTo?.code ? t('coar_why_role_pinned', { role: label, code: op.pinnedTo.code }) : t('coar_why_role_pinned_none', { role: label });
    }
    if (rule === 'role-contra') return t('coar_why_role_contra', { type: editRoles?.type || '' });
    return t(`coar_why_${String(rule).replace(/-/g, '_')}`, {
      classes: (op?.classes || []).map(c => t(CLASS_KEY[c] || c)).join(' / '), code: op?.heldBy?.code || '' });
  };
  /** (R1 review, second pass) The sentence(s) the chosen role is allowed with: a role moved off an account with posted lines
   *  — they stay there; from now on the role posts to the new account. From GET /chart/role-options (`moves`), never worked
   *  out here; '' when the choice is the stored one, refused, or moves nothing posted. */
  const roleMoveWarning = (a: Account): string => {
    if (!editRoles || edit.role === (a.role || '')) return '';
    const chosen = edit.role === '' ? editRoles.none : editRoles.options.find(o => o.role === edit.role);
    if (!chosen || chosen.refused) return '';
    const roleName = (r: string) => t(`coar_role_${r}`, { defaultValue: editRoles.options.find(o => o.role === r)?.label || r });
    // (R1 re-verify, v3) …and the account it goes to: its own posted lines are read as the role's balance from now on.
    return (chosen.moves || []).flatMap(m => [
      ...(m.posted > 0 && m.from ? [t('coar_move_warning', { code: m.from, n: m.posted, to: m.to, role: roleName(m.role) })] : []),
      ...((m.toPosted || 0) > 0 ? [t('coar_move_warning_to', { code: m.to, n: m.toPosted, role: roleName(m.role) })] : []),
    ]).join(' ');
  };
  /** What a refused save says: a role rule (400 { rule }) or the administrator-only door (403 admin_only) in this language,
   *  anything else as the server said it. */
  const saveError = (e: any, role?: string) => {
    const p = e?.payload || {};
    if (Number(e?.status) === 403 && p.code === 'admin_only') return t('coar_admin_only');
    const rule = String(p.rule || '');
    if (rule && role !== undefined && i18n.exists(`coar_why_${rule.replace(/-/g, '_')}`)) return t('coar_refused', { why: roleWhy(rule, role) });
    return e?.message || '';
  };
  /** The Role select's options: '' (no role of its own — saying so when the account still holds the template's), then every
   *  role, each one the server refuses greyed with its reason, and one another account holds named with that account. */
  const roleOptionEls = (o: RoleOptions, a: Account) => {
    const noneLabel = a.roleSource === 'template' && a.roleEffective ? t('coar_none_template', { role: t(`coar_role_${a.roleEffective}`) }) : t('coar_none');
    // (R1 review) Taking its own role off is refused when the role would go back to a copy that cannot hold it, or to none:
    // greyed with the reason, like any refused choice (the stored value stays selectable as itself).
    const noneRefused = o.none?.refused || null;
    const none = noneRefused ? t('coar_unavailable', { role: noneLabel, why: roleWhy(noneRefused, '') }) : noneLabel;
    return [<option key="-" value="" disabled={!!noneRefused && (a.role || '') !== ''} data-refused={noneRefused || undefined}
      title={noneRefused ? o.none?.message : undefined}>{none}</option>, ...o.options.map(op => {
      const label = t(`coar_role_${op.role}`, { defaultValue: op.label });
      const shown = op.refused ? t('coar_unavailable', { role: label, why: roleWhy(op.refused, op.role) })
        : op.heldBy && op.heldBy.id !== a.id ? t('coar_held_by', { role: label, code: op.heldBy.code }) : label;
      return <option key={op.role} value={op.role} disabled={!!op.refused && op.role !== (a.role || '')} data-refused={op.refused || undefined}
        title={op.refused ? op.message : undefined}>{shown}</option>;
    })];
  };
  /** The words a type select offers, each refused one greyed out with the server's reason in words. */
  const typeOptionEls = (o: TypeOptions | null, current: string) => {
    const opts = o?.options || [{ word: '', kind: '' as ChartKind, refused: null, message: '' }];
    const els = opts.map(op => {
      const label = op.word || t('coat_type_none');
      const why = op.refused ? t(`coat_why_${op.refused.replace(/-/g, '_')}`, {
        classes: (o?.classesOf?.[op.word] || []).map(c => t(CLASS_KEY[c] || c)).join(' / '),
      }) : '';
      return <option key={op.word || '-'} value={op.word} disabled={!!op.refused && op.word !== current} data-refused={op.refused || undefined}
        title={op.refused ? op.message : undefined}>{op.refused ? t('coat_unavailable', { word: label, why }) : label}</option>;
    });
    // A stored word the list does not have (written before the rules knew it) still shows as itself.
    if (current && !opts.some(op => op.word === current)) els.push(<option key={`cur-${current}`} value={current}>{current}</option>);
    return els;
  };
  /** b13 S2: the Parent select's options — the server's list, in its tree order, indented by depth; the top level first. */
  const parentOptionEls = (o: ParentOptions) => o.options.map(op => (
    <option key={op.id || '-'} value={op.id}>
      {op.id ? `${'\u2014 '.repeat(op.depth)}${op.code} · ${op.nameTh || op.nameEn}` : t('coat_parent_top')}
    </option>
  ));
  /** b13 S2: a tree rule's reason in this language (coat_why_<rule>), the server's sentence when the key is unknown. */
  const treeWhy = (rule: string, message = '') => t(`coat_why_${String(rule).replace(/-/g, '_')}`, { defaultValue: message || rule, classes: '' });
  /** (b13 review) A stored parent the Parent select does not offer, in words: its code and name when it is an account of
   *  this chart (looked up by id among the rows the server sent), else that it is not one. Never the id itself. */
  const storedParentLabel = (parentId: string) => {
    const p = (rows || []).find(r => r.id === parentId);
    return p ? t('coat_parent_stored', { code: p.code, name: p.name_th || p.name_en }) : t('coat_parent_missing');
  };
  /** (b13 review) Why the tree withholds Remove from a row — the server's verdict (remove_refusal), in this language. Only
   *  the tree's own answers: the older holders (lines, a box, …) keep the silence they always had. */
  // (Batch 8 R1 review) …and role-held: an account an administrator gave a role keeps it until the role is taken off.
  const TREE_REMOVE = ['has-children', 'header-needs-children', 'header-needs-postable', 'role-held'];
  const removeWhy = (a: Account) => (!readOnly && !a.lines && !a.boxes && !!a.remove_refusal && TREE_REMOVE.includes(a.remove_refusal) ? a.remove_refusal : '');
  /** The words for a withheld Remove: role-held on an account holding its role FROM THE TEMPLATE (R1 review, second pass —
   *  removed, the role would have no account) has its own sentence, naming the role. */
  const removeWords = (a: Account) => (removeWhy(a) === 'role-held' && !a.role && a.roleSource === 'template' && a.roleEffective
    ? t('coat_why_role_held_template', { role: t(`coar_role_${a.roleEffective}`) }) : treeWhy(removeWhy(a), a.remove_message));
  const groupListId = `coat-groups-${clientId}`;
  const toggle = async (a: Account) => {
    setBusy(a.id); setErr('');
    try { await api.acctUpdateAccount(a.id, { active: !a.active }); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const remove = async (a: Account) => {
    if (!window.confirm(t('pa_chart_remove_confirm', { code: a.code, name: a.name_th }))) return;
    setBusy(a.id); setErr('');
    try { await api.acctDeleteAccount(a.id); await load(); }
    catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };

  // The front-end search: code, both names, the description, the group and the type word.
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rows || []).filter(r => (!onlyUnclassified || r.kind === '')
      // (Batch 8 R1) …and the role it holds, in this language's words.
      && (!q || [r.code, r.name_th, r.name_en, r.description, r.account_group, r.account_type, r.roleEffective ? t(`coar_role_${r.roleEffective}`) : '']
        .some(v => String(v || '').toLowerCase().includes(q))));
  }, [rows, query, onlyUnclassified, t]);
  const unclassified = (rows || []).filter(r => r.kind === '').length;
  // b13 S2: while the search or the "not classified" filter narrows the list, it is drawn flat — an indentation under a
  // filtered list would claim a parent that is not above it (the general ledger's rule under a column sort, B13-20).
  const flat = !!query.trim() || onlyUnclassified;

  if (rows === null) return null;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 13px', flexWrap: 'wrap' }}>
        <i className="ti ti-list-numbers" style={{ fontSize: 18, color: 'var(--accent)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{t('pa_chart_title')}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>
            {rows.length ? t('pa_chart_counts', { n: rows.length, used: rows.filter(r => r.lines).length }) : removedAll ? t('coas_removed_all') : t('pa_chart_empty')}
            {/* How many accounts nobody has given a type yet (kind '' — the server's answer), and a
                one-click filter to just those. They still post as every account always has.
                ⛔ Kept while the filter is ON even when the count reaches 0 — classifying the last one
                with the filter on would otherwise leave an empty list and nothing that turns it off. */}
            {(unclassified > 0 || onlyUnclassified) && (
              <button type="button" data-unclassified-filter={onlyUnclassified ? 'on' : 'off'} title={t('coat_unclassified_tip')}
                onClick={() => { setOnlyUnclassified(v => !v); setOpen(true); }}
                style={{ marginLeft: 8, padding: '0 7px', fontSize: 10.5, fontWeight: 700, borderRadius: 999, cursor: 'pointer',
                  border: '1px solid #F5D9A8', background: onlyUnclassified ? '#F5D9A8' : '#FEF6E7', color: '#92400E' }}>
                {onlyUnclassified ? t('coat_show_all') : t('coat_unclassified', { n: unclassified })}
              </button>
            )}
          </span>
        </span>
        {/* First initialisation: the whole template, for any accounting login — only while the chart has
            never been started (a chart whose accounts were all removed is not started again). */}
        {!readOnly && !rows.length && !removedAll && <button data-copy-template onClick={copy} disabled={!!busy} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>
          <i className={busy === 'copy' ? 'ti ti-loader' : 'ti ti-copy'} aria-hidden /> {t('pa_chart_copy_template')}
        </button>}
        {/* Download sits left of Open/Hide: puppeteer takes a second or two, so the icon spins for
            exactly as long as the server is rendering — the wait the owner asked to make visible. */}
        {!!rows.length && <button onClick={downloadPdf} disabled={!!busy} style={btn} title={t('pa_chart_download')}>
          <i className={busy === 'pdf' ? 'ti ti-loader' : 'ti ti-download'}
            style={{ display: 'inline-block', animation: busy === 'pdf' ? 'spin 0.7s linear infinite' : undefined }} aria-hidden /> {t('pa_chart_download')}
        </button>}
        {(!!rows.length || removedAll) && <button onClick={() => setOpen(o => !o)} style={btn}>{open ? t('pa_chart_hide') : t('pa_chart_open')}</button>}
      </div>

      {err && <div data-chart-error style={{ fontSize: 12.5, color: '#B42318', background: '#FCEBEB', borderTop: '1px solid #F3B9B9', padding: '8px 13px' }}>{err}</div>}

      {/* ── A presentation choice, asked once ───────────────────────────────────────────────────
          Rendered OUTSIDE the fold, because inside StatutoryBooks the chart starts folded and a
          question nobody can see is the defect this exists to fix.

          ⛔ It names the two placements and what each does to the gross profit THIS screen's own
          statement computes, and stops. It asserts no rule, cites nothing, and highlights neither
          button, because the choice is the client's under its own reporting framework and the
          system does not make it. */}
      {questions.map(q => (
        <div key={q.accountId} style={{ borderTop: '1px solid #F5D9A8', background: '#FEF9F0', padding: '10px 13px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
            <i className="ti ti-help-circle" style={{ fontSize: 15, color: '#92400E' }} aria-hidden />
            <span style={{ fontSize: 12.5, fontWeight: 800, color: '#92400E' }}>{t('pa_chart_wd_title')}</span>
            <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
              {t('pa_chart_wd_account', { code: q.code, name: q.nameTh, amount: baht(Math.abs(q.amount)) })}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-primary)', marginTop: 5 }}>{t('pa_chart_wd_q')}</div>
          <div style={{ display: 'flex', gap: 9, marginTop: 8, flexWrap: 'wrap' }}>
            {!readOnly && ([['trading', 'pa_chart_wd_trading', 'pa_chart_wd_trading_hint'],
               ['other', 'pa_chart_wd_other', 'pa_chart_wd_other_hint']] as const).map(([value, label, hint]) => (
              <button key={value} onClick={() => answer(q, value)} disabled={busy === q.accountId}
                style={{ ...btn, textAlign: 'left', maxWidth: 330, whiteSpace: 'normal', lineHeight: 1.35 }}>
                <span style={{ display: 'block' }}>{t(label)}</span>
                <span style={{ display: 'block', fontWeight: 500, fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>{t(hint)}</span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#92400E', marginTop: 7 }}>{t('pa_chart_wd_unanswered')}</div>
          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>{t('pa_chart_wd_note')}</div>
        </div>
      ))}

      {open && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 13px 13px' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7, marginBottom: 9, flexWrap: 'wrap' }}>
            <input data-chart-search value={query} onChange={e => setQuery(e.target.value)} placeholder={t('coat_search_ph')}
              aria-label={t('coat_search_ph')} style={{ ...inp, flex: '1 1 220px', minWidth: 180 }} />
            {/* Opens the preview; nothing is copied by this button (batch 5 round 7). Never for a books login. */}
            {!readOnly && <button data-sync-open onClick={() => setSyncing(v => !v)} disabled={!!busy} title={t('pa_chart_sync_title')}
              style={{ ...btn, background: syncing ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
              <i className="ti ti-copy" aria-hidden /> {t('pa_chart_sync_template')}
            </button>}
            {!readOnly && <button data-add-open onClick={() => setAdding(a => !a)} style={{ ...btn, background: adding ? 'var(--bg-active)' : 'var(--bg-surface)' }}>
              <i className="ti ti-plus" aria-hidden /> {t('pa_chart_add_account')}
            </button>}
          </div>

          {!readOnly && syncing && <TemplateSync clientId={clientId} onChanged={load} onClose={() => setSyncing(false)} />}

          {adding && (
            <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center', padding: 10, borderRadius: 8, background: 'var(--bg-app)' }}>
              <input data-add-code value={draft.code} onChange={e => setDraft({ ...draft, code: e.target.value })} placeholder={t('pa_chart_ph_code')} style={{ ...inp, width: 80 }} />
              <input data-add-name-th value={draft.nameTh} onChange={e => setDraft({ ...draft, nameTh: e.target.value })} placeholder={t('pa_chart_ph_name_th')} style={{ ...inp, width: 200 }} />
              <input value={draft.nameEn} onChange={e => setDraft({ ...draft, nameEn: e.target.value })} placeholder={t('pa_chart_ph_name_en')} style={{ ...inp, width: 180 }} />
              <select data-add-class value={draft.cls} onChange={e => setDraft({ ...draft, cls: e.target.value, parentId: '' })} style={inp}>
                {ORDER.map(c => <option key={c} value={c}>{t(CLASS_KEY[c] || c)}</option>)}
              </select>
              {/* b13 S2: where the new account sits — the server's choices for this class (asked again on a class change). */}
              {addParents && addParents.cls === draft.cls && (
                <select data-add-parent value={draft.parentId} onChange={e => setDraft({ ...draft, parentId: e.target.value })}
                  aria-label={t('coat_parent')} title={t('coat_parent_why')} style={inp}>
                  {parentOptionEls(addParents)}
                </select>
              )}
              <select value={draft.cfSection} onChange={e => setDraft({ ...draft, cfSection: e.target.value })}
                style={{ ...inp, borderColor: cfMatters(draft.cls) && !draft.cfSection ? '#B45309' : (inp.border as string) }}>
                {CF.map(c => <option key={c.k} value={c.k}>{c.k ? t('pa_chart_cf_prefixed', { section: t(c.key) }) : t(c.key)}</option>)}
              </select>
              {/* Only income and expense: a balance-sheet account is not on a trading account at all. */}
              {fsMatters(draft.cls) && (
                <select value={draft.fsSection} onChange={e => setDraft({ ...draft, fsSection: e.target.value })} style={inp}>
                  {FS.map(c => <option key={c.k} value={c.k}>{c.k ? t('pa_chart_fs_prefixed', { section: t(c.key) }) : t(c.key)}</option>)}
                </select>
              )}
              <select data-add-type value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })} aria-label={t('coat_type')} style={inp}>
                {typeOptionEls(addOpts, draft.type)}
              </select>
              <input data-add-group value={draft.group} onChange={e => setDraft({ ...draft, group: e.target.value })} list={groupListId}
                placeholder={t('coat_group_ph')} aria-label={t('coat_group')} style={{ ...inp, width: 200 }} />
              <input data-add-description value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })}
                placeholder={t('coat_description_ph')} aria-label={t('coat_description')} style={{ ...inp, width: 260 }} />
              <button data-add-save onClick={add} disabled={busy === 'add'} style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }}>{t('pa_chart_save')}</button>
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('pa_chart_name_th_manual')}</span>
              {cfMatters(draft.cls) && !draft.cfSection && (
                <span style={{ fontSize: 11, color: '#92400E', width: '100%' }}>
                  {t('pa_chart_cf_missing_warn')}
                </span>
              )}
            </div>
          )}

          {/* Every group word offered so far: the master template's Subgroups, then this client's own. */}
          <datalist id={groupListId}>{(vocab?.groups || []).map(g => <option key={g} value={g} />)}</datalist>
          {(query.trim() || onlyUnclassified) && !shown.length && (
            <div data-chart-no-match style={{ fontSize: 12, color: 'var(--text-tertiary)', padding: '6px 2px 10px' }}>{t('coat_no_match')}</div>
          )}
          {ORDER.filter(c => shown.some(r => r.class === c)).map(cls => (
            <div key={cls} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 5 }}>
                {t(CLASS_KEY[cls] || cls)}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ fontSize: 10.5, color: 'var(--text-tertiary)', textAlign: 'left' }}>
                    <th style={{ padding: '2px 8px', fontWeight: 600 }}>{t('coat_col_code')}</th>
                    <th style={{ padding: '2px 8px', fontWeight: 600 }}>{t('coat_col_account')}</th>
                    <th style={{ padding: '2px 8px', fontWeight: 600 }}>{t('coat_col_group_type')}</th>
                    <th data-col-role style={{ padding: '2px 8px', fontWeight: 600 }} title={t('coar_col_role_tip')}>{t('coar_col_role')}</th>
                    <th style={{ padding: '2px 8px', fontWeight: 600 }} title={t('coat_normal_tip')}>{t('coat_col_normal')}</th>
                    <th style={{ padding: '2px 8px', fontWeight: 600, textAlign: 'right' }}>{t('coat_col_balance')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {shown.filter(r => r.class === cls).map(a => (<React.Fragment key={a.id}>
                    <tr data-account-row={a.code} data-depth={flat ? 0 : (a.depth || 0)} style={{ borderTop: '1px solid var(--border)', opacity: a.active ? 1 : 0.5 }}>
                      {/* b13 S2: indented by the server's depth inside its class — a sub-account under its parent (flat
                          while a search narrows the list). */}
                      <td style={{ padding: '6px 8px', paddingLeft: a.depth && !flat ? 8 + 14 * a.depth : 8, width: 62, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {!!a.depth && !flat && <i className="ti ti-corner-down-right" aria-hidden style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 3 }} />}{a.code}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {(
                          <>
                            <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{a.name_th}</span>
                            <span style={{ marginLeft: 8, color: 'var(--text-tertiary)' }}>{a.name_en}</span>
                            {cfMatters(a.class) && !a.cf_section && (
                              <span title={t('pa_chart_cf_missing_title')} style={{ marginLeft: 7, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8', padding: '0 6px', borderRadius: 999 }}>{t('pa_chart_cf_missing_pill')}</span>
                            )}
                            {/* ⛔ Only where there is MONEY on the account. Every client's chart has
                                income and expense accounts nobody has placed and no money on; a pill
                                on each of those is noise that teaches people to ignore pills. This is
                                the same test the statement applies before it lists an account. */}
                            {fsMatters(a.class) && !a.fs_section && !!a.net_dr && (
                              <span title={t('pa_chart_fs_missing_title')} style={{ marginLeft: 7, fontSize: 10, fontWeight: 800, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8', padding: '0 6px', borderRadius: 999 }}>{t('pa_chart_fs_missing_pill')}</span>
                            )}
                            {a.description && <span data-description style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2, whiteSpace: 'pre-wrap' }}>{a.description}</span>}
                            {/* b13 S2: a parent stored behind the doors that breaks a tree rule — said, never hidden; the
                                account is walked as a root until a person fixes it here. */}
                            {!!a.tree_problem && <span data-tree-problem={a.tree_problem} style={{ display: 'block', fontSize: 11, color: '#92400E', marginTop: 2 }}>
                              <i className="ti ti-alert-triangle" aria-hidden /> {t('coat_tree_problem', { why: treeWhy(a.tree_problem) })}</span>}
                          </>
                        )}
                      </td>
                      {/* The group word and the type word, as written (never translated), and the badge
                          the SERVER's kind gives the type; a control account (control ar / ap) wears the
                          Control badge whatever its type says. */}
                      <td data-group-type style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                        {a.account_group && <span data-group lang="en" style={{ color: 'var(--text-secondary)', marginRight: 6 }}>{a.account_group}</span>}
                        <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <TypeBadge type={a.account_type} kind={a.kind} />
                          <ControlBadge role={a.control} />
                        </span>
                      </td>
                      {/* Batch 8 R1: the role it holds (the server's roleEffective / roleSource) and the posting maps' answers
                          naming it — read by everyone, changed only in an administrator's Edit row. */}
                      <td data-role-cell style={{ padding: '6px 8px', maxWidth: 240 }}>
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                          {!!a.roleEffective && <RoleBadge role={a.roleEffective} source={a.roleSource || ''} />}
                          {(a.roleMapped || []).map(m => <MappedRoleBadge key={`${m.source}:${m.role}`} source={m.source} role={m.role} />)}
                        </span>
                      </td>
                      <td data-normal style={{ padding: '6px 8px', fontSize: 11, color: 'var(--text-tertiary)', width: 44 }} title={t('coat_normal_tip')}>{a.normalSide}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                        {a.lines ? baht(Math.abs(a.net_dr)) : ''}
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', width: 230 }}
                        data-remove-why={removeWhy(a) || undefined} title={removeWhy(a) ? removeWords(a) : undefined}>
                        {editId !== a.id && (<>
                          {/* Who changed this account, when, and what it was before (COA-10). Shown to
                              every login; the drawer itself tells a books login it may not read it. */}
                          <button onClick={() => setHistoryOf(a)} title={t('coal_history_tip')} data-history-of={a.code}
                            style={{ ...btn, padding: '3px 7px', fontSize: 11, marginRight: 5 }}>
                            <i className="ti ti-history" aria-hidden /> {t('coal_history')}</button>
                          {!readOnly && (<>
                            <button data-edit-of={a.code} onClick={() => startEdit(a)} title={t('coat_edit_tip')} style={{ ...btn, padding: '3px 7px', fontSize: 11 }}>{t('coat_edit')}</button>
                            <button onClick={() => toggle(a)} disabled={busy === a.id} style={{ ...btn, padding: '3px 7px', fontSize: 11, marginLeft: 5 }}>{a.active ? t('pa_chart_switch_off') : t('pa_chart_switch_on')}</button>
                            {/* Only an account nothing has ever been posted to can go: the ledger and
                                the printed books still cite the others. (Batch 11 W4, the gate review) Nor one a petty
                                cash box holds (`boxes`): the server refuses it — switched off instead. */}
                            {/* b13 S2: …nor one the server says it would refuse (remove_refusal: sub-accounts, a Header's
                                last sub-account, the holders above) — a request bound to be refused is never offered. */}
                            {!a.lines && !a.boxes && !a.remove_refusal && <button onClick={() => remove(a)} disabled={busy === a.id} data-remove-of={a.code} style={{ ...btn, padding: '3px 7px', fontSize: 11, marginLeft: 5, color: '#B42318' }}>
                              <i className="ti ti-trash" aria-hidden /></button>}
                          </>)}
                        </>)}
                      </td>
                    </tr>
                    {editId === a.id && (
                      <tr data-edit-row={a.code}>
                        <td colSpan={7} style={{ padding: '4px 8px 10px' }}>
                          <div data-edit-form style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', padding: 9, borderRadius: 8, background: 'var(--bg-app)' }}>
                            <input value={edit.nameTh} onChange={e => setEdit({ ...edit, nameTh: e.target.value })} aria-label={t('pa_chart_ph_name_th')} style={{ ...inp, width: 190 }} />
                            <input value={edit.nameEn} onChange={e => setEdit({ ...edit, nameEn: e.target.value })} aria-label={t('pa_chart_ph_name_en')} style={{ ...inp, width: 170 }} />
                            {/* The class only while nothing is posted: printed books cite a posted class. b13 S2 (B13-35): nor
                                on an account inside a tree — any other class would be a parent-class refusal — which says so. */}
                            {!a.lines && !a.parent_id && !a.children && (
                              /* (b13 review) A class change clears a parent picked in this form, as Add does: it is of the old
                                 class, so the server would refuse it (parent-class), and the new class's choices do not hold
                                 it. The select is offered only on an account with no stored parent, so '' is its own value. */
                              <select data-edit-class value={edit.cls} onChange={e => setEdit({ ...edit, cls: e.target.value, parentId: '' })} aria-label={t('coat_class')} style={inp}>
                                {/* Batch 8 R1: an account holding a role is offered only the classes that role stands on (the
                                    server's roleClasses — any other is a role-class refusal); its own class always. */}
                                {ORDER.filter(c => !(a.roleClasses || []).length || (a.roleClasses || []).includes(c) || c === a.class)
                                  .map(c => <option key={c} value={c}>{t(CLASS_KEY[c] || c)}</option>)}
                              </select>
                            )}
                            {!a.lines && (!!a.parent_id || !!a.children) && (
                              <span data-class-locked style={{ fontSize: 11, color: 'var(--text-secondary)', maxWidth: 360 }}>
                                {t(CLASS_KEY[a.class] || a.class)} — {t('coat_class_tree_locked')}</span>
                            )}
                            {/* b13 S2: where it sits — the server's choices, or why it cannot leave its parent at all. */}
                            {/* (b13 review) …and only the answer for the class the form NOW says: while a class change's answer
                                is on its way, the old class's choices (each a parent-class refusal now) are not offered. */}
                            {editParents && editParents.cls === edit.cls && (editParents.movable
                              ? <span data-parent-locked={editParents.movable.rule} style={{ fontSize: 11, color: '#92400E', maxWidth: 360 }}>
                                  {t('coat_parent')}: {t(`coat_parent_locked_${String(editParents.movable.rule).replace(/-/g, '_')}`, { defaultValue: editParents.movable.message })}</span>
                              : <select data-edit-parent value={edit.parentId} onChange={e => setEdit({ ...edit, parentId: e.target.value })}
                                  aria-label={t('coat_parent')} title={t('coat_parent_why')} style={inp}>
                                  {parentOptionEls(editParents)}
                                  {/* A stored parent the list does not offer (one that breaks a rule as stored) still shows as the
                                      choice in place — in words, never its id (b13 review). */}
                                  {!!edit.parentId && !editParents.options.some(o => o.id === edit.parentId) && <option value={edit.parentId} data-parent-stored>{storedParentLabel(edit.parentId)}</option>}
                                </select>)}
                            <select data-edit-type value={edit.type} onChange={e => setEdit({ ...edit, type: e.target.value })} aria-label={t('coat_type')} style={inp}>
                              {typeOptionEls(editOpts, a.account_type || '')}
                            </select>
                            {/* Batch 8 R1 (D1): the role — only for a login the server lets change one, and only with the server's
                                answer for the class the form now says. Nobody else is drawn a role control. */}
                            {canEditRole && editRoles && editRoles.cls === (edit.cls || a.class) && (<>
                              <select data-edit-role value={edit.role} onChange={e => setEdit({ ...edit, role: e.target.value })}
                                aria-label={t('coar_col_role')} title={t('coar_select_why')} style={{ ...inp, maxWidth: 420 }}>
                                {roleOptionEls(editRoles, a)}
                              </select>
                              {/* (R1 review, second pass) A move with posted history left behind is taken with one sentence,
                                  shown before Save. */}
                              {!!roleMoveWarning(a) && (
                                <div data-role-warning role="status" style={{ flexBasis: '100%', fontSize: 12, color: 'var(--warning, #b45309)', lineHeight: 1.4 }}>
                                  <i className="ti ti-alert-triangle" aria-hidden /> {roleMoveWarning(a)}</div>)}
                            </>)}
                            <input data-edit-group value={edit.group} onChange={e => setEdit({ ...edit, group: e.target.value })} list={groupListId}
                              placeholder={t('coat_group_ph')} aria-label={t('coat_group')} style={{ ...inp, width: 200 }} />
                            <select value={edit.cfSection} onChange={e => setEdit({ ...edit, cfSection: e.target.value })} style={inp}>
                              {CF.map(c => <option key={c.k} value={c.k}>{c.k ? t('pa_chart_cf_prefixed', { section: t(c.key) }) : t(c.key)}</option>)}
                            </select>
                            {/* Where this account's money sits on บัญชีทำการ — the same answer the
                                prompt above records, reachable per account for the rest of them.
                                Offered only for the two classes that can hold one. */}
                            {fsMatters(edit.cls || a.class) && (
                              <select value={edit.fsSection} onChange={e => setEdit({ ...edit, fsSection: e.target.value })} style={inp}>
                                {FS.map(c => <option key={c.k} value={c.k}>{c.k ? t('pa_chart_fs_prefixed', { section: t(c.key) }) : t(c.key)}</option>)}
                              </select>
                            )}
                            <textarea data-edit-description value={edit.description} onChange={e => setEdit({ ...edit, description: e.target.value })}
                              placeholder={t('coat_description_ph')} aria-label={t('coat_description')} rows={2} style={{ ...inp, width: '100%', resize: 'vertical' }} />
                            <button data-edit-save onClick={() => saveEdit(a)} disabled={busy === a.id} style={{ ...btn, padding: '4px 9px' }}>{t('pa_chart_save')}</button>
                            <button onClick={() => setEditId('')} style={{ ...btn, padding: '4px 9px' }}>{t('pa_chart_cancel')}</button>
                            <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>{t('coat_words_note')}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
      {historyOf && <AccountHistory account={historyOf} onClose={() => setHistoryOf(null)} />}
    </div>
  );
}
